import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';

import { useAppDispatch, useAppSelector, useAppStore } from '@/lib/hooks';
import { setModalName, setModalVisible } from '@/lib/slices/modal-slice';

import ModalComponent from '@/common/components/modals/ModalComponent';
import Button from '@/common/components/ui/Button';
import StandardInputText from '@/common/components/input/StandardInputText';
import StandardInputCheckbox from '@/common/components/input/StandardInputCheckbox';
import { CancelIcon, CopyIcon } from '@/assets/icons/button';

import CourseEditService from '@/services/course/creation/course-edit-service';
import { CourseData } from 'course-types';
import LoadingSpinner from '../../LoadingSpinner';

export default function CourseCopyWizard() {
  const store = useAppStore();
  const dispatch = useAppDispatch();

  const modalVisible: boolean = useAppSelector(() => store.getState().modal.visible);
  const modalName: string = useAppSelector(() => store.getState().modal.name);

  const activeCourseData: CourseData = useAppSelector(
    () => store.getState().course.activeCourseData,
  );

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [copyCourseName, setCopyCourseName] = useState<string>('');
  const [copyActivities, setCopyActivities] = useState<boolean>(false);

  useEffect(() => {
    if (modalVisible && modalName == 'CopyCourse') {
      setCopyCourseName(`Copy of ${activeCourseData.name}`);
    }
  }, [modalVisible, modalName, activeCourseData.name]);

  const handleCopyCourseNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCopyCourseName(event.target.value);
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      await CourseEditService.handleCopyCourse(
        dispatch,
        handleClose,
        copyCourseName,
        activeCourseData,
        copyActivities,
      );
    } catch (error) {
      toast.error('Failed to copy course');
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setCopyCourseName('');
    setCopyActivities(false);
    setIsLoading(false);
    dispatch(setModalVisible(false));
    dispatch(setModalName(''));
  };

  return (
    <ModalComponent
      isOpen={modalVisible && modalName == 'CopyCourse'}
      onRequestClose={handleClose}
      contentLabel='Copy Course'
      contentSubtitle={`Create a copy of "${activeCourseData.name}"`}
      className='max-w-[500px] w-full'
      closeOnClickOff={!isLoading}
    >
      <div className='relative w-full'>
        <div className='flex flex-col gap-[16px] w-full'>
          <div className='flex flex-col gap-2 bg-surface_gray_200 rounded-lg p-4 border border-border_gray_300'>
            <p className='text-base'>Create a copy of this course with a new name.</p>
            <p className='text-sm text-text_gray_900'>
              You can choose to copy the course structure only, or include all content and
              activities.
            </p>
          </div>

          <StandardInputText
            id='copyCourseName'
            label='Course Name'
            isRequired={true}
            value={copyCourseName}
            onChange={handleCopyCourseNameChange}
            disabled={isLoading}
          />

          <StandardInputCheckbox
            id='copyActivities'
            label='Copy Course Content'
            checked={copyActivities}
            onChange={(e) => setCopyActivities(e.target.checked)}
            disabled={isLoading}
            description='Include all content and activities in the copy'
          />

          <div className='w-full pt-4 flex flex-col gap-2'>
            <div className='flex flex-row gap-8 py-2'>
              <Button onClick={handleSubmit} disabled={!copyCourseName.trim() || isLoading}>
                <CopyIcon className='w-[20px] h-[20px] mr-2' /> Copy Course
              </Button>
              <Button outline onClick={handleClose} disabled={isLoading}>
                <CancelIcon className='w-[20px] h-[20px] mr-2' /> Cancel
              </Button>
            </div>
          </div>
        </div>

        {isLoading && (
          <div className='absolute top-0 left-0 z-10 w-full h-full flex items-center justify-center'>
            <div className='absolute inset-0 bg-surface_gray_50 opacity-70' />
            <div className='flex flex-col items-center justify-center gap-2 text-sm z-20'>
              <LoadingSpinner />
              <div>Copying course...</div>
            </div>
          </div>
        )}
      </div>
    </ModalComponent>
  );
}
